import { eq, Property, toTuple } from '@debbie/common/dist/query/index.js'
import deletedImage from '../../assets/images/deleted_image.svg'
import { c, h } from '@debbie/cortex/dist/index.js'
import { useQuery } from '@debbie/cortex/dist/query/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import * as db from '@debbie/common/dist/query/db.js'
import {
  Errors,
  Loading,
  Panel,
  PlaceholderLoader,
  Select,
  UserPreview,
} from '@debbie/iris/dist/components/index.js'
import { FilterMenuV2 } from '@debbie/iris/dist/components/FilterV2/FilterMenuV2.js'
import { OutsideClick } from '@debbie/iris/dist/components/OutsideClick.js'
import { useAtom } from 'jotai'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { viewPatches } from './state.js'
import {
  AugmentedFilter,
  ViewDescription,
} from '@debbie/common/dist/views/index.js'
import { styled } from 'styled-components'
import THEME from '@debbie/iris/dist/theme.js'
import { genProperties, toFilters } from './utils.js'
import { CaseSummary } from '../../components/cases/index.js'
import { baseContext, displays, views } from './views.js'
import CaseLoader from '@debbie/iris/dist/components/CaseLoader.js'
import {
  ArrowLeft,
  ArrowRight,
  Settings,
} from '@debbie/iris/dist/components/Icons/index.js'
import {
  ActivityType,
  SocketUser,
} from '@debbie/common/dist/web-sockets/index.js'
import { selectTenant } from '@debbie/cortex/dist/me/selectors.js'
import { DataType } from '@debbie/common/dist/query/types.js'
import { CustomComponent, DisplayComponent } from './DisplayComponents.js'
import { selectCaseById } from '@debbie/cortex/dist/cases/selectors.js'

export const ViewQueue = (props: { id: string; itemId: string }) => {
  const lang = useSelector((s: RootState) => s.lang)

  const dataLoader = useQuery('views')({
    base: 'views',
    properties: {
      views: {
        fields: ['id', 'name', 'options', 'visibility'],
      },
    },
    filters: eq('views.id', props.id),
    orderBy: 'createdAt',
    orderByDirection: 'DESC',
    limit: 1,
    offset: 0,
  })
  const [localPatches, setLocalPatches] = useAtom(viewPatches)

  const [filters, setFilters] = useState<AugmentedFilter[]>(
    localPatches[props.id]?.filters ?? [],
  )

  useEffect(() => {
    if (h.hasData(dataLoader) && localPatches[props.id] == null) {
      const view = dataLoader.data.items[0].views

      setFilters(view.options.filters)
    } else if (
      localPatches[props.id] != null &&
      filters != localPatches[props.id]?.filters
    ) {
      setFilters(localPatches[props.id]?.filters ?? [])
    }
  }, [dataLoader.loadingState, localPatches[props.id]])

  const [initial, setInitial] = useState(true)
  useEffect(() => {
    if (initial) {
      setInitial(false)
    } else if (
      h.hasData(dataLoader) &&
      filters != dataLoader.data.items[0].views.options.filters
    ) {
      const viewDescription = dataLoader.data.items[0].views.options
      setLocalPatches((lp) => {
        const desc = lp[props.id] ?? viewDescription
        return {
          ...lp,
          [props.id]: {
            ...desc,
            filters,
          },
        }
      })
    }
  }, [filters])

  if (h.isError(dataLoader)) {
    if (dataLoader.error instanceof Error) {
      return <Errors errors={[dataLoader.error.message]} />
    }
    return <Errors errors={[lang.misc.errorOccured]} />
  }

  if (h.hasData(dataLoader)) {
    const view = dataLoader.data.items[0].views
    return (
      <Wrapper>
        <FilterWrapper>
          {h.hasData(dataLoader) ? (
            <FilterMenuV2
              base={view.options.base}
              filters={filters}
              setFilters={setFilters}
            />
          ) : (
            <PlaceholderLoader />
          )}
          {localPatches[props.id] != null ? (
            <LocalChanges>
              <span>{lang.views.localChanges}</span>
              {' - '}
              <Reset
                onClick={() => {
                  setLocalPatches((lp) => {
                    return { ...lp, [props.id]: undefined }
                  })
                }}
              >
                {lang.views.resetLocalChanges}
              </Reset>
            </LocalChanges>
          ) : null}
        </FilterWrapper>
        <Queue
          viewDescription={localPatches[props.id] ?? view.options}
          selected={props.itemId}
          onChange={(viewDesc) => {
            setLocalPatches((lp) => {
              return { ...lp, [props.id]: viewDesc }
            })
          }}
        />
        <Content>
          <Contented>
            <Preview itemId={props.itemId} />
          </Contented>
        </Content>
      </Wrapper>
    )
  }

  return <PlaceholderLoader />
}

const Preview = (props: { itemId: string }) => {
  // TODO: previews for multiple bases
  const lang = useSelector((s: RootState) => s.lang)
  const singleCase = useSelector((s: RootState) =>
    selectCaseById(s, props.itemId),
  )

  return (
    <>
      <CaseLoader noRedirect id={props.itemId} />
      {singleCase && singleCase.deleted ? (
        <ErrorWrapper>
          <ErrorImage src={deletedImage} />
          <h1>{lang.cases.caseHasBeenDeleted}</h1>
        </ErrorWrapper>
      ) : (
        <CaseSummary id={props.itemId} />
      )}
    </>
  )
}

const Queue = (props: {
  selected: string
  viewDescription: ViewDescription
  onChange: (vd: ViewDescription) => void
}) => {
  const lang = useSelector((s: RootState) => s.lang)
  const tenant = useSelector(selectTenant)
  const activeUsers: {
    [tenantId: string]: SocketUser[]
  } = useSelector((s: RootState) => s.activeUsers)

  const caseActiveMap: { [caseId: string]: SocketUser[] | undefined } = {}
  if (tenant != null && activeUsers[tenant.id] != null) {
    for (const user of activeUsers[tenant.id]) {
      for (const activity of user.activities) {
        if (activity.type === ActivityType.WATCHINGCASE) {
          if (caseActiveMap[activity.handle] == null) {
            caseActiveMap[activity.handle] = []
          }

          caseActiveMap[activity.handle]!.push(user)
        }
      }
    }
  }

  const [page, setPage] = useState(0)
  const pageSize = 100

  const [settingsOpen, setSettingsOpen] = useState(false)

  const ctx = baseContext[props.viewDescription.base]

  const preview = {
    header: props.viewDescription.preview?.header ?? ctx?.preview.header,
    content: props.viewDescription.preview?.content ?? ctx?.preview.content,
  }

  const { properties, scopedProperties } = genProperties(
    props.viewDescription.base,
    props.viewDescription.displayOptions,
    preview,
  )

  const dataLoader = useQuery(props.viewDescription.base)({
    base: props.viewDescription.base,
    properties: properties,
    scoped: scopedProperties,
    // @ts-expect-error
    orderBy: props.viewDescription?.orderBy ?? 'createdAt',
    orderByDirection: props.viewDescription?.orderByDirection ?? 'DESC',
    offset: page * pageSize,
    limit: pageSize,
    filters: toFilters(props.viewDescription.filters),
  })

  if (h.isError(dataLoader)) {
    if (dataLoader.error instanceof Error) {
      return <Errors errors={[dataLoader.error.message]} />
    }
    return <Errors errors={[lang.misc.errorOccured]} />
  }

  return (
    <>
      <Items>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div></div>
          <SettingsIcon
            onClick={() => {
              // TODO: remove when clicking outside
              setSettingsOpen((o) => !o)
            }}
          />
        </div>
        {settingsOpen && (
          <QueueSettings
            viewDescription={props.viewDescription}
            onChange={props.onChange}
            onClose={() => setSettingsOpen(false)}
          />
        )}
        {h.isLoading(dataLoader) ? <PlaceholderLoader height="10px" /> : null}
        {h.hasData(dataLoader) ? (
          <>
            {dataLoader.data.items.length === 0 ? (
              <>
                <br></br>
                <br></br>
                <span>{lang.views.noData}</span>
              </>
            ) : null}
            {dataLoader.data.items.map((data) => {
              let id: string | null = null

              let header = null
              let content = null
              if (ctx != null) {
                const [table, col] = toTuple(ctx.itemId)
                // @ts-expect-error
                id = data[table][col]

                const headerView = preview.header
                  ? views[preview.header as keyof typeof views]
                  : null
                const contentView = preview.content
                  ? views[preview.content as keyof typeof views]
                  : null

                if (headerView == null) {
                } else if ('customDisplay' in headerView) {
                  header = React.createElement(
                    // @ts-expect-error
                    displays[headerView.customDisplay],
                    { value: data },
                  )
                } else {
                  const [table, col] = toTuple(headerView.value)

                  // @ts-expect-error
                  const type = db.schema.entities[table].properties[col]
                    .type as DataType

                  const Comp =
                    CustomComponent[
                      headerView.value as Property<typeof db.schema>
                    ] ?? DisplayComponent[type.type]

                  const d = data[table]

                  header = !Array.isArray(d) ? (
                    // @ts-expect-error
                    React.createElement(Comp, { value: d[col] })
                  ) : (
                    <>
                      {d.map((d: any) => {
                        return (
                          <div>
                            {React.createElement(Comp, { value: d[col] })}
                          </div>
                        )
                      })}
                    </>
                  )
                }

                if (contentView == null) {
                } else if ('customDisplay' in contentView) {
                  content = React.createElement(
                    // @ts-expect-error
                    displays[contentView.customDisplay],
                    { value: data },
                  )
                } else {
                  const [table, col] = toTuple(contentView.value)

                  // @ts-expect-error
                  const type = db.schema.entities[table].properties[col]
                    .type as DataType

                  const Comp =
                    CustomComponent[
                      contentView.value as Property<typeof db.schema>
                    ] ?? DisplayComponent[type.type]

                  const d = data[table]

                  content = !Array.isArray(d) ? (
                    // @ts-expect-error
                    React.createElement(Comp, { value: d[col] })
                  ) : (
                    <>
                      {d.map((d: any) => {
                        return (
                          <div>
                            {React.createElement(Comp, { value: d[col] })}
                          </div>
                        )
                      })}
                    </>
                  )
                }
              }

              return (
                <Item
                  key={id ?? undefined}
                  selected={`${id}` === props.selected}
                  onClick={() => {
                    if (id) {
                      c.redirect.redirect(`${id}`)
                    }
                  }}
                >
                  <ItemHeader>{header}</ItemHeader>
                  <ItemContent>{content}</ItemContent>
                  <Others>
                    {props.viewDescription.base === 'cases' &&
                    // Id is there because it is used in the preview
                    // @ts-expect-error
                    data.cases.id != null &&
                    // @ts-expect-error
                    caseActiveMap[data.cases.id] != null
                      ? // @ts-expect-error
                        caseActiveMap[data.cases.id]!.map((user) => {
                          return <UserPreview user={user} />
                        })
                      : null}
                  </Others>
                </Item>
              )
            })}
          </>
        ) : null}
        <PageStub></PageStub>
        <PageWrapper>
          <Left
            onClick={() => {
              setPage((p) => Math.max(p - 1, 0))
            }}
          />
          <PageIndicator>{page + 1}</PageIndicator>
          <Right
            onClick={() => {
              setPage((p) => p + 1)
            }}
          />
        </PageWrapper>
      </Items>
    </>
  )
}

const QueueSettings = (props: {
  viewDescription: ViewDescription
  onChange: (viewDescription: ViewDescription) => void
  onClose: () => void
}) => {
  const lang = useSelector((s: RootState) => s.lang)
  const settingsRef = useRef<HTMLDivElement>(null)

  const ctx = baseContext[props.viewDescription.base]

  return (
    <OutsideClick
      reference={settingsRef}
      isOpen
      onOutsideClick={() => {
        props.onClose()
      }}
    >
      <QueueSettingsWrapper ref={settingsRef}>
        <label>
          <span>{lang.views.orderedBy}</span>
          <Select
            value={props.viewDescription.orderBy}
            onChange={(e) => {
              const orderBy = e.currentTarget.value
              props.onChange({ ...props.viewDescription, orderBy })
            }}
          >
            {ctx?.availableForOrdering.map((view) => {
              return <option value={view.key}>{view.label(lang)}</option>
            })}
          </Select>
        </label>
        <label>
          <span>{lang.views.orderedByDirection}</span>
          <Select
            value={props.viewDescription.orderByDirection}
            onChange={(e) => {
              const orderByDirection = e.currentTarget.value as 'ASC' | 'DESC'
              props.onChange({ ...props.viewDescription, orderByDirection })
            }}
          >
            <option value="DESC">{lang.misc.descending}</option>
            <option value="ASC">{lang.misc.ascending}</option>
          </Select>
        </label>
        <label>
          <span>{lang.views.primaryLabel}</span>
          <Select
            value={props.viewDescription.preview?.header ?? ctx?.preview.header}
            onChange={(e) => {
              const header = e.currentTarget.value
              props.onChange({
                ...props.viewDescription,
                preview: {
                  ...props.viewDescription.preview,
                  header,
                },
              })
            }}
          >
            {ctx?.availableForQueue.map((view) => {
              return <option value={view}>{lang.viewLabels[view]}</option>
            })}
          </Select>
        </label>
        <label>
          <span>{lang.views.secondaryLabel}</span>
          <Select
            value={
              props.viewDescription.preview?.content ?? ctx?.preview.content
            }
            onChange={(e) => {
              const content = e.currentTarget.value
              props.onChange({
                ...props.viewDescription,
                preview: {
                  ...props.viewDescription.preview,
                  content,
                },
              })
            }}
          >
            {ctx?.availableForQueue.map((view) => {
              return <option value={view}>{lang.viewLabels[view]}</option>
            })}
          </Select>
        </label>
      </QueueSettingsWrapper>
    </OutsideClick>
  )
}

const Wrapper = styled.div`
  display: grid;
  width: 100%;
  height: calc(100vh - 60px);
  grid-template-columns: 300px 1fr;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    'top top'
    'left main';

  color: ${THEME.COLOR_PRIMARY};
`

const FilterWrapper = styled.div`
  grid-area: top;

  background: ${THEME.COLOR_SECONDARY};
  padding: 4px;
  border-radius: 8px;
  margin: 8px;
  margin-bottom: 4px;
`

const Items = styled.div`
  grid-area: left;
  padding: 4px;
  padding-left: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  height: 100%;
  overflow-y: auto;
  position: relative;
`

const Item = styled.div<{ selected: boolean }>`
  background: ${(props) =>
    props.selected ? THEME.COLOR_ACCENT : THEME.COLOR_SECONDARY};
  padding: 14px;
  width: 100%;
  border-radius: 8px;
  cursor: pointer;
  color: ${(props) =>
    props.selected ? THEME.COLOR_SECONDARY : THEME.COLOR_PRIMARY};
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ItemHeader = styled.h2`
  margin: 0;
  font-size: 1rem;
`
const ItemContent = styled.p`
  margin: 0;
  font-size: 0.8rem;
`

const Content = styled.div`
  grid-area: main;
  padding: 4px;
  overflow-y: auto;
`

const Contented = styled.div`
  overflow-y: auto;
  max-width: 1400px;
  margin: 0px auto;
  padding: 16px;
`

const LocalChanges = styled.div`
  padding: 0.2em;
  border-radius: 4px;
  margin-bottom: 1em;
  font-size: 0.8em;
  height: 1em;
`

const Reset = styled.span`
  font-weight: bold;
  cursor: pointer;
`

const PageStub = styled.div`
  display: block;
  padding: 1.2em;
`

const PageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: fixed;
  bottom: 8px;
  border-radius: 8px;
  background: ${THEME.COLOR_SECONDARY};
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.3);
  width: calc(300px - 12px);
  height: 2em;
  padding: 0px 8px;
  left: 8px;
`

const Left = styled(ArrowLeft)`
  cursor: pointer;
`

const Right = styled(ArrowRight)`
  cursor: pointer;
`

const PageIndicator = styled.span`
  font-size: 0.8em;
  user-select: none;
`

const Others = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 0;
`

const ErrorWrapper = styled.div`
  padding: 50px 0;

  h1 {
    text-align: center;
  }

  p {
    text-align: center;
    font-size: 1.2em;
    opacity: 0.7;
  }
`

const ErrorImage = styled.img`
  width: 100%;
  max-width: 50%;
  display: block;
  margin: 60px auto;
`

const SettingsIcon = styled(Settings)`
  cursor: pointer;
`

const QueueSettingsWrapper = styled.div`
  z-index: 10;
  position: absolute;
  top: 1.5em;
  right: 0.25em;
  background: #fff;
  padding: 1em;
  border-radius: 0.5em;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.3);
`
